import { t } from 'i18next';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// From ai-builder-utils, NOT chat-message: that module pulls react-markdown
// (pure ESM) and would make this store untestable under Jest
import { AiBuilderChatMessage, windowKeepingIntent } from './ai-builder-utils';

import { toast } from '@/components/ui/use-toast';
import { maskTypedSecrets } from '@activepieces/shared';

interface ChatState {
  chats: Record<string, AiBuilderChatMessage[]>;
  /**
   * Flows with a turn running in THIS page. Never persisted: a turn lives on
   * this page's socket, so after a reload nothing runs here — which is exactly
   * what tells an answer that was lost from one still on its way.
   */
  turnsInFlight: Record<string, boolean>;
  addMessage: (flowId: string, msg: AiBuilderChatMessage) => void;
  setMessages: (flowId: string, msgs: AiBuilderChatMessage[]) => void;
  clearMessages: (flowId: string) => void;
  startTurn: (flowId: string) => void;
  endTurn: (flowId: string) => void;
}

/** Enough to reopen a conversation where it was left, not to archive it */
const MAX_PERSISTED_MESSAGES = 60;

/**
 * localStorage is shared with the whole app and can be full for reasons that
 * have nothing to do with this store. A write that cannot happen must not take
 * the turn down with it: the conversation then lives for the session only.
 */
const quotaSafeStorage = createJSONStorage(() => ({
  getItem: (name: string) => localStorage.getItem(name),
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      toast({
        title: t('Chat history not saved'),
        description: t(
          'This conversation stays available until you close the tab.',
        ),
      });
    }
  },
  removeItem: (name: string) => localStorage.removeItem(name),
}));

const maskStoredSecrets = (
  message: AiBuilderChatMessage,
): AiBuilderChatMessage => ({
  ...message,
  content: maskTypedSecrets(message.content),
  contentPrefix:
    message.contentPrefix && maskTypedSecrets(message.contentPrefix),
  sourcePrompt: message.sourcePrompt && maskTypedSecrets(message.sourcePrompt),
});

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      chats: {},
      turnsInFlight: {},
      addMessage: (flowId, msg) =>
        set((state) => ({
          chats: {
            ...state.chats,
            [flowId]: state.chats[flowId]
              ? [...state.chats[flowId], msg]
              : [msg],
          },
        })),
      setMessages: (flowId, msgs) =>
        set((state) => ({
          chats: {
            ...state.chats,
            [flowId]: msgs,
          },
        })),
      clearMessages: (flowId) =>
        set((state) => ({
          chats: {
            ...state.chats,
            [flowId]: [],
          },
        })),
      startTurn: (flowId) =>
        set((state) => ({
          turnsInFlight: { ...state.turnsInFlight, [flowId]: true },
        })),
      endTurn: (flowId) =>
        set((state) => ({
          turnsInFlight: { ...state.turnsInFlight, [flowId]: false },
        })),
    }),
    {
      name: 'ai-builder-chat-history',
      // A full history cannot be kept: the quota is shared with the rest of the
      // app, and a conversation grows without bound. What a reload needs is the
      // recent exchange, not the archive.
      storage: quotaSafeStorage,
      // A conversation stored before secrets were masked at send time holds
      // them in clear, model echoes included: masked as it is read back
      merge: (persisted, current) => {
        const { chats = {} } = (persisted ?? {}) as Partial<
          Pick<ChatState, 'chats'>
        >;
        return {
          ...current,
          chats: Object.fromEntries(
            Object.entries(chats).map(([flowId, messages]) => [
              flowId,
              messages.map(maskStoredSecrets),
            ]),
          ),
        };
      },
      // Flow versions ride on messages for the SESSION's undo/retry only:
      // persisting them stores the whole flow twice per bot message and blows
      // the localStorage quota after a few conversations. Directives too: a
      // reopened chat replays them only for a turn that ended in this session.
      partialize: (state) => ({
        chats: Object.fromEntries(
          Object.entries(state.chats).map(([flowId, messages]) => [
            flowId,
            windowKeepingIntent(messages, MAX_PERSISTED_MESSAGES).map(
              ({
                previousFlowVersion: _previousFlowVersion,
                newFlowVersion: _newFlowVersion,
                navigateDirectives: _navigateDirectives,
                ...rest
              }) => rest,
            ),
          ]),
        ),
      }),
    },
  ),
);
